import { getStatusCode, getErrorMessage } from '@/lib/axios'
import { getDownloadURL, ref } from 'firebase/storage'
import { storage } from '@/lib/firebase'

export * from './membership'
export * from './feature-flag'
export * from './time'
export * from './branch'
export * from './csv'
export * from './phoneExport'
export * from './concurrency'
export * from './fullName'

export function getTimeDifferenceLabel(
  start: string,
  end: string
): { diff: number; type: 'minute' | 'hour' } {
  const [startHour, startMinute] = start.split(':').map(Number)
  const [endHour, endMinute] = end.split(':').map(Number)

  const startTotalMinutes = startHour * 60 + startMinute
  const endTotalMinutes = endHour * 60 + endMinute

  const diff = endTotalMinutes - startTotalMinutes

  if (diff <= 60) {
    return { diff, type: 'minute' }
  } else {
    const hours = diff / 60
    return { diff: hours, type: 'hour' }
  }
}

export function formatNumber(num: number, decimal?: number): string {
  return num.toFixed(decimal).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export function simulateFileInputChange(
  file: File,
  callback: (e: React.ChangeEvent<HTMLInputElement>) => void
) {
  const input = document.createElement('input')
  input.type = 'file'

  const dataTransfer = new DataTransfer()
  dataTransfer.items.add(file)
  input.files = dataTransfer.files

  // Now create a real ChangeEvent
  const event = new Event('change', { bubbles: true })
  input.dispatchEvent(event)

  // Manually call callback with a properly typed event
  const reactEvent = {
    ...event,
    target: input,
    currentTarget: input,
  } as unknown as React.ChangeEvent<HTMLInputElement>

  callback(reactEvent)
}

export function formatExpireDays(day?: number) {
  if (!day) return '-'

  if (day % 365 === 0) {
    const years = day / 365
    return years === 1 ? `${years} Year` : `${formatNumber(years)} Years`
  }

  if (day % 30 === 0) {
    const months = day / 30
    return months === 1 ? `${months} Month` : `${formatNumber(months)} Months`
  }

  return day === 1 ? `${day} Day` : `${formatNumber(day)} Days`
}

export function formatExpireUnit(day?: number): {
  divideDay: number
  dayUnit: 'day' | 'month' | 'year'
} {
  const d = day ?? 0
  if (d === 0) return { divideDay: 0, dayUnit: 'day' }
  if (d % 365 === 0) return { divideDay: Math.floor(d / 365), dayUnit: 'year' }
  if (d % 30 === 0) return { divideDay: Math.floor(d / 30), dayUnit: 'month' }
  return { divideDay: d, dayUnit: 'day' }
}

export function formatFileSize(size: number, decimal = 2): string {
  const units = ['B', 'KB', 'MB', 'GB']
  let i = 0
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024
    i++
  }
  return `${size.toFixed(decimal)} ${units[i]}`
}

export function deduplicate<T extends { value: unknown }>(options: T[]) {
  return options.filter((opt, idx, self) => self.findIndex((o) => o.value === opt.value) === idx)
}

export function capitalizeFirstLetter(str: string): string {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export function formatValueStringToLabel(value?: string): string {
  const splitWord = value?.split(value?.includes('_') ? '_' : '-')
  return splitWord?.map((word) => capitalizeFirstLetter(word)).join(' ') ?? ''
}

const ROLE_GROUP_LABEL_OVERRIDES: Record<string, string> = {
  'cs-sales': 'CS & Sale',
}

export function formatRoleGroupLabel(group?: string): string {
  if (!group) return ''
  return ROLE_GROUP_LABEL_OVERRIDES[group] ?? formatValueStringToLabel(group)
}

// E.164 to TH format (e.g., +66912345678 to 0912345678)
export const fromE164TH = (input?: string): string => {
  const s = (input ?? '').trim()
  if (!s) return ''

  // Only accept valid Thai mobile E.164
  if (/^\+66\d+$/.test(s)) {
    return '0' + s.slice(3)
  }

  if (/^66\d+$/.test(s)) {
    return '0' + s.slice(2)
  }

  return s
}

export const toE164TH = (input: string, onError?: () => void): string => {
  const raw = input.trim()

  // Allow only digits, dash, and optional leading +
  if (!/^\+?[\d-]+$/.test(raw)) {
    onError?.()
    throw new Error('invalid-phone-number')
  }

  // Remove dash before validation
  const digits = raw.replace(/-/g, '')

  let normalized: string | null = null

  // 0XXXXXXXXX
  if (/^0\d+$/.test(digits)) {
    normalized = `+66${digits.slice(1)}`
  }
  // 66XXXXXXXXX
  else if (/^66\d+$/.test(digits)) {
    normalized = `+${digits}`
  }
  // +66XXXXXXXXX (already E.164)
  else if (/^\+66\d+$/.test(raw)) {
    normalized = raw
  }

  if (!normalized) {
    onError?.()
    throw new Error('invalid-phone-number')
  }

  return normalized
}

export function stringValue(s?: string | number) {
  return s && String(s).trim().length ? String(s) : '-'
}

export function toOrdinal(num: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd']
  const v = num % 100
  return num + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0])
}

export function parseFormattedNumber(value: string | number): number {
  if (typeof value !== 'string') return Number(value)

  // remove thousand separators, spaces, currency symbols
  const cleaned = value.replace(/[^0-9.-]/g, '')
  return cleaned === '' ? NaN : Number(cleaned)
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.style.display = 'none'

  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)

  window.URL.revokeObjectURL(url)
}

export const isConflictReferral = (error: any) => {
  return (
    getStatusCode(error) === 409 &&
    (getErrorMessage(error).includes('ลีด') || getErrorMessage(error).includes('referral'))
  )
}

export const isConflictRoomName = (error: any) => {
  return (
    getStatusCode(error) === 409 &&
    (getErrorMessage(error).includes('room_name') || getErrorMessage(error).includes('name'))
  )
}

export const isConflictEmail = (error: any) => {
  return getStatusCode(error) === 409 && getErrorMessage(error).includes('email')
}

export const isConflictCitizenId = (error: any) => {
  return (
    getStatusCode(error) === 409 &&
    (getErrorMessage(error).includes('citizen_id') || getErrorMessage(error).includes('citizenId'))
  )
}

export async function getFirebaseStorageUrl(filePath: string): Promise<string> {
  return getDownloadURL(ref(storage, filePath))
}

/**
 * Formats a phone number string into the format "098-2567890".
 * It handles inputs like "+66982567890" by replacing "+66" with "0".
 *
 * @param phone - The phone number string to format.
 * @returns The formatted phone number string.
 */

export const formatPhoneNumber = (phone: string | null | undefined): string => {
  if (!phone) return ''
  let cleanPhone = phone.replace(/^\+66/, '0')
  cleanPhone = cleanPhone.replace(/\D/g, '')

  // Thai mobile numbers are typically 10 digits
  if (cleanPhone.length === 10) {
    return `${cleanPhone.slice(0, 3)}-${cleanPhone.slice(3)}`
  }

  return cleanPhone
}
